import { getPrivateKey } from './user.service';
import Orders from '../sidechain/handles/orderHandle';
import ProductReviews from '../sidechain/handles/productReviewHandle';
import CustomError from './errors/CustomError';
import { NOTIFY_CATEGORY, NOTIFY_CONTENT_TYPE, status_response } from './constants';
import { getUserProfile } from './profile.service';
import { changeReadStatusNotificationByOrderOfCustomer } from './notification.service';

/**
 * Get status review to check customer can review product or not: //0: cannot review, 1: can review, 2: already reviewed
 * @param {*} orderId order id
 * @return {*} status review
 */
const checkCanReview = async function(orderId) {
  try {
    const privateKey = await getPrivateKey();
    const productReviewHandle = new ProductReviews(privateKey);
    const result = await productReviewHandle.checkCanReview(orderId);
    return {
      status: status_response.success,
      result: {
        data: result,
        messageId: 'success'
      }
    };
  } catch (error) {
    return {
      status: status_response.error,
      result: {
        message: error.message,
        messageId: error.messageId
      }
    };
  }
};

/**
 * Customer create review for product
 * @param orderId
 * @param {*} status
 * @param {*} title
 * @param {*} content
 */
const createReview = async function(orderId, status, title, content) {
  try {
    const privateKey = await getPrivateKey();
    const productReviewHandle = new ProductReviews(privateKey);

    // Check can review before create
    const statusReview = await productReviewHandle.checkCanReview(orderId);
    if (statusReview === 0) {
      throw new CustomError("doesn't have permission to review product.", 'EF2143_Content');
    }
    if (statusReview === 2) {
      console.log('Already review this product.');
      // Can still review product after
    }

    const orderHandle = new Orders(privateKey);
    const order = await orderHandle.getOrderByID(orderId);
    if (order.shopAddr === order.cusAddr) {
      throw new CustomError('Cannot review by self.', 'EF2143_Content');
    }
    // Add review
    const data = await productReviewHandle.createReview(orderId, status, title, content);

    // Change status of previous notification related to this orderId from Shop to Read
    await changeReadStatusNotificationByOrderOfCustomer(orderId);

    return {
      status: status_response.success,
      result: {
        data: data,
        messageId: 'success'
      }
    };
  } catch (error) {
    console.log(error);
    return {
      status: status_response.error,
      result: {
        message: error.message,
        messageId: error.messageId || 'EF2143_Content'
      }
    };
  }
};

/**
 * Get detail review by id
 * @param {*} reviewId
 * @return {*} review information
 *  * sample data for review:
 * {
 *  id: 1
 *  shopAddr: "0xd359a0D9A8613B7FFaf510761720cC83FfAf52EA"
 *  reviewType: 1
 *  cusAddr: "0x61DA61fF02a195E5d4b6F7cd0B737Cd7D793Dbc7"
 *  status: 1
 *  content: "This is my review to product"
 *  createdAt: 1544500281
 *  updatedAt: 1544500281
 * }
 */
const getDetailReview = async function(reviewId) {
  try {
    let privateKey;
    try {
      privateKey = await getPrivateKey();
    } catch (error) {
      //error
    }
    const productReviewHandle = new ProductReviews(privateKey);
    const result = await productReviewHandle.getReviewById(reviewId);
    return {
      status: status_response.success,
      result: {
        data: result,
        messageId: 'success'
      }
    };
  } catch (error) {
    return {
      status: status_response.error,
      result: {
        message: error.message,
        messageId: error.messageId || 'EF2143_Content'
      }
    };
  }
};

/**
 * Get list review product
 * @param {*} productId
 * @param {*} page
 * @param {*} limit
 * @param {*} sortByDate 0: old -> new, 1: new -> old
 * @return array of review
 * sample data return:
 * {
 *  reviews: [{
 *    id: 1
 *    shopAddr: "0xd359a0D9A8613B7FFaf510761720cC83FfAf52EA"
 *    reviewType: 1
 *    cusAddr: "0x61DA61fF02a195E5d4b6F7cd0B737Cd7D793Dbc7"
 *    status: 1
 *    content: "This is my review to product"
 *    createdAt: 1544500281
 *    updatedAt: 1544500281
 *  }],
 *  count: 1
 * }
 */
const getReviews = async function(productId, page, limit, sortByDate = 1) {
  try {
    let privateKey;
    try {
      privateKey = await getPrivateKey();
    } catch (error) {
      //error
    }
    const productReviewHandle = new ProductReviews(privateKey);
    const result = await productReviewHandle.getReviews(productId, page, limit, sortByDate);
    return {
      status: status_response.success,
      result: {
        data: result,
        messageId: 'success'
      }
    };
  } catch (error) {
    return {
      status: status_response.error,
      result: {
        message: error.message,
        messageId: error.messageId || 'EF2143_Content'
      }
    };
  }
};

/**
 * Get statistic review about a customer
 * @param {*} productId
 * @return statistic review
 * sample data for statistic review:
 * {
 *   numOfGood: 1,
 *   numOfBad: 0
 * }
 */
const getStatisticReview = async function(productId) {
  try {
    let privateKey;
    try {
      privateKey = await getPrivateKey();
    } catch (error) {
      //error
    }
    const productReviewHandle = new ProductReviews(privateKey);
    const result = await productReviewHandle.getStatisticReview(productId);
    return {
      status: status_response.success,
      result: {
        data: result,
        messageId: 'success'
      }
    };
  } catch (error) {
    return {
      status: status_response.error,
      result: {
        message: error.message,
        messageId: error.messageId
      }
    };
  }
};

export { checkCanReview, createReview, getDetailReview, getReviews, getStatisticReview };
